import React, { useMemo, useCallback } from 'react';
import SdkAgGrid from '@tls/ui-aggrid';

function _extends() {
  return _extends = Object.assign ? Object.assign.bind() : function (n) {
    for (var e = 1; e < arguments.length; e++) {
      var t = arguments[e];
      for (var r in t) ({}).hasOwnProperty.call(t, r) && (n[r] = t[r]);
    }
    return n;
  }, _extends.apply(null, arguments);
}

const CACHE_BLOCK_SIZE = 50;
const serverSideColumnOptions = {
  filter: 'agTextColumnFilter'
};

const ServerSideAgGrid = _ref => {
  let {
    columnDefs,
    ...rest
  } = _ref;
  const columnDefinitions = useMemo(() => columnDefs.map(columnDef => ({
    ...serverSideColumnOptions,
    ...columnDef
  })), [columnDefs]);
  return /*#__PURE__*/React.createElement(SdkAgGrid, _extends({
    columnDefs: columnDefinitions,
    rowModelType: "serverSide",
    cacheBlockSize: CACHE_BLOCK_SIZE
  }, rest));
};

const parseRowCount = value => {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }
  if (typeof value === 'string' && value.trim() !== '') {
    const parsedValue = Number.parseInt(value, 10);
    return Number.isFinite(parsedValue) ? parsedValue : undefined;
  }
  return undefined;
};
const normalizeFetchResult = result => {
  var _ref, _ref2, _result$rowData, _result$rowCount;
  if (Array.isArray(result)) {
    return {
      rowData: result
    };
  }
  if (!result || typeof result !== 'object') {
    return {
      rowData: []
    };
  }
  const rowData = (_ref = (_ref2 = (_result$rowData = result.rowData) !== null && _result$rowData !== void 0 ? _result$rowData : result.data) !== null && _ref2 !== void 0 ? _ref2 : result.rows) !== null && _ref !== void 0 ? _ref : [];
  const rowCount = parseRowCount((_result$rowCount = result.rowCount) !== null && _result$rowCount !== void 0 ? _result$rowCount : result.total);
  return {
    rowData,
    ...(rowCount !== undefined ? {
      rowCount
    } : {})
  };
};
const reportSuccess = (params, _ref3) => {
  let {
    rowData,
    rowCount
  } = _ref3;
  const loadSuccessParams = {
    rowData: rowData !== null && rowData !== void 0 ? rowData : []
  };
  const parsedRowCount = parseRowCount(rowCount);
  if (parsedRowCount !== undefined) {
    loadSuccessParams.rowCount = parsedRowCount;
  }
  if (typeof params.success === 'function') {
    params.success(loadSuccessParams);
    return;
  }
  if (typeof params.successCallback === 'function') {
    params.successCallback(loadSuccessParams.rowData, loadSuccessParams.rowCount);
  }
};
const reportFail = params => {
  if (typeof params.fail === 'function') {
    params.fail();
    return;
  }
  if (typeof params.failCallback === 'function') {
    params.failCallback();
  }
};
const setServerSideDatasource = (api, datasource) => {
  if (typeof api.setGridOption === 'function') {
    api.setGridOption('serverSideDatasource', datasource);
    return;
  }
  if (typeof api.setServerSideDatasource === 'function') {
    api.setServerSideDatasource(datasource);
  }
};
const useGridServerSideDataSource = _ref4 => {
  let {
    fetchData,
    initialRows = false
  } = _ref4;
  const createDatasource = useCallback(() => {
    let initialRowsUsed = false;
    return {
      getRows: async params => {
        if (!initialRowsUsed && initialRows) {
          reportSuccess(params, normalizeFetchResult(initialRows));
          initialRowsUsed = true;
          return;
        }
        try {
          const result = await fetchData({
            limit: params.request.endRow - params.request.startRow,
            offset: params.request.startRow,
            sortModel: params.request.sortModel,
            filterModel: params.request.filterModel
          });
          reportSuccess(params, normalizeFetchResult(result));
        } catch (error) {
          reportFail(params);
        }
      }
    };
  }, [fetchData, initialRows]);
  const onGridReady = useCallback(_ref5 => {
    let {
      api
    } = _ref5;
    setServerSideDatasource(api, createDatasource());
  }, [createDatasource]);
  return {
    onGridReady
  };
};

export { CACHE_BLOCK_SIZE, ServerSideAgGrid as default, serverSideColumnOptions, useGridServerSideDataSource };
